<template>
  <div class="site-safety-health-measures">
    <b-container fluid>
      <b-row class="mb-2">
        <b-col>
          <BackToListLink />
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col>
          <h2>安全衛生指示</h2>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col md="6">
          <b-form-group label="現場" label-for="site-select">
            <b-form-select
              id="site-select"
              v-model="selectedSiteId"
              :options="siteOptions"
              @change="onSiteChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="対象月" label-for="target-month">
            <b-form-input
              id="target-month"
              v-model="targetMonth"
              type="month"
              @change="onMonthChange"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col>
          <b-button
            variant="primary"
            @click="downloadSafetyPosterExcel"
            :disabled="!selectedSiteId || !targetMonth"
          >
            エクセルダウンロード
          </b-button>
        </b-col>
      </b-row>

      <SummaryForm
        :siteId="selectedSiteId"
        :targetMonth="targetMonth"
        :isManagedUser="isManagedUser"
      />

      <b-card class="mb-4" title="現場への安全衛生指示">
        <b-form v-if="isManagedUser" @submit.prevent="saveSiteMeasure">
          <b-table-simple bordered small responsive head-variant="light">
            <b-thead>
              <b-tr>
                <b-th class="text-center" style="width: 23%;">工種名</b-th>
                <b-th class="text-center" style="width: 23%;">作業名</b-th>
                <b-th class="text-center" style="width: 22%;">危険性・有害性（予想される災害）</b-th>
                <b-th class="text-center" style="width: 22%;">除去・低減策（防止対策）</b-th>
                <b-th class="text-center" style="width: 10%;">KY転記</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr>
                <b-td>
                  <b-form-input
                    id="site-measure-work-type-name"
                    v-model="siteMeasureForm.workTypeName"
                  />
                </b-td>
                <b-td>
                  <b-form-input
                    id="site-measure-work-name"
                    v-model="siteMeasureForm.workName"
                  />
                </b-td>
                <b-td>
                  <b-form-textarea
                    id="site-measure-hazards"
                    v-model="siteMeasureForm.hazards"
                    rows="3"
                  />
                </b-td>
                <b-td>
                  <b-form-textarea
                    id="site-measure-prevention-measures"
                    v-model="siteMeasureForm.preventionMeasures"
                    rows="3"
                  />
                </b-td>
                <b-td class="text-center align-middle">
                  <b-form-checkbox
                    v-model="siteMeasureForm.copyToKy"
                    switch
                  />
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
          <b-row class="mt-2">
            <b-col>
              <b-button type="submit" variant="primary">
                {{ siteMeasureForm.id ? '更新' : '保存' }}
              </b-button>
              <b-button v-if="siteMeasureForm.id" @click="cancelSiteMeasureEdit" variant="secondary" class="ml-2">
                キャンセル
              </b-button>
            </b-col>
          </b-row>
        </b-form>

        <b-table-simple class="mt-3" bordered small responsive striped hover>
          <b-thead head-variant="light">
            <b-tr>
              <b-th v-if="isManagedUser" class="text-center" style="width: 40px;"></b-th>
              <b-th>工種名</b-th>
              <b-th>作業名</b-th>
              <b-th>危険性・有害性</b-th>
              <b-th>除去・低減策</b-th>
              <b-th>契約会社</b-th>
              <b-th>責任者</b-th>
              <b-th>KY転記</b-th>
              <b-th>操作</b-th>
            </b-tr>
          </b-thead>
          <draggable
            v-model="siteMeasures"
            tag="tbody"
            handle=".drag-handle"
            @end="onDragEnd"
            :disabled="!isManagedUser"
          >
            <b-tr v-for="measure in siteMeasures" :key="measure.id">
              <b-td v-if="isManagedUser" class="text-center align-middle drag-handle" style="cursor: grab;">
                ☰
              </b-td>
              <b-td>{{ measure.workTypeName }}</b-td>
              <b-td>{{ measure.workName }}</b-td>
              <b-td>{{ measure.hazards }}</b-td>
              <b-td>{{ measure.preventionMeasures }}</b-td>
              <b-td>
                <div v-if="measure.responsibles && measure.responsibles.length > 0">
                  <div v-for="responsible in measure.responsibles" :key="responsible.id" class="mb-1">
                    {{ responsible.contractCompanyName || '-' }}
                  </div>
                </div>
                <span v-else>-</span>
              </b-td>
              <b-td>
                <div v-if="measure.responsibles && measure.responsibles.length > 0">
                  <div v-for="responsible in measure.responsibles" :key="responsible.id" class="mb-1">
                    {{ responsible.responsibleName || '-' }}
                  </div>
                </div>
                <span v-else>-</span>
              </b-td>
              <b-td class="text-center align-middle">
                <b-form-checkbox
                  v-if="isManagedUser"
                  :checked="measure.copyToKy"
                  switch
                  @change="toggleCopyToKy(measure)"
                  :disabled="isTogglingCopyToKy === measure.id"
                />
                <b-badge v-else :variant="measure.copyToKy ? 'success' : 'secondary'">
                  {{ measure.copyToKy ? 'あり' : 'なし' }}
                </b-badge>
              </b-td>
              <b-td>
                <b-button v-if="isManagedUser" size="sm" @click="openResponsibleModal(measure)" variant="success" class="mr-1">
                  責任者管理
                </b-button>
                <b-button v-if="isManagedUser" size="sm" @click="openEditMeasureModal(measure)" variant="info" class="mr-1">
                  編集
                </b-button>
                <b-button v-if="isManagedUser" size="sm" @click="deleteSiteMeasure(measure)" variant="danger">
                  削除
                </b-button>
              </b-td>
            </b-tr>
          </draggable>
        </b-table-simple>
      </b-card>

      <b-modal
        id="responsible-modal"
        :title="'責任者管理 - ' + (selectedMeasure ? (selectedMeasure.workTypeName || '') + ' ' + (selectedMeasure.workName || '') : '')"
        size="lg"
        @hidden="onResponsibleModalHidden"
      >
        <b-form @submit.prevent="saveResponsible">
          <b-row>
            <b-col md="6">
              <b-form-group label="契約会社" label-for="responsible-contract-company">
                <b-form-select
                  id="responsible-contract-company"
                  v-model="responsibleForm.contractCompanyId"
                  :options="siteContractCompanyOptions"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="責任者名" label-for="responsible-name">
                <b-form-input
                  id="responsible-name"
                  v-model="responsibleForm.responsibleName"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-button type="submit" variant="primary">
                {{ responsibleForm.id ? '更新' : '追加' }}
              </b-button>
              <b-button v-if="responsibleForm.id" @click="cancelResponsibleEdit" variant="secondary" class="ml-2">
                キャンセル
              </b-button>
            </b-col>
          </b-row>
        </b-form>

        <b-table
          class="mt-3"
          :items="measureResponsibles"
          :fields="responsibleFields"
          striped
          hover
          responsive
        >
          <template #cell(contractCompanyName)="row">
            {{ row.item.contractCompanyName || '-' }}
          </template>
          <template #cell(responsibleName)="row">
            {{ row.item.responsibleName || '-' }}
          </template>
          <template #cell(actions)="row">
            <b-button size="sm" @click="editResponsible(row.item)" variant="info" class="mr-1">
              編集
            </b-button>
            <b-button size="sm" @click="deleteResponsible(row.item)" variant="danger">
              削除
            </b-button>
          </template>
        </b-table>

        <template #modal-footer>
          <b-button variant="secondary" @click="$bvModal.hide('responsible-modal')">
            閉じる
          </b-button>
        </template>
      </b-modal>

      <b-modal
        id="edit-measure-modal"
        :title="'安全衛生指示の編集'"
        size="xl"
        @hidden="onEditMeasureModalHidden"
      >
        <b-form @submit.prevent="updateMeasureFromModal">
          <b-card class="mb-3" title="基本情報">
            <b-row>
              <b-col md="6">
                <b-form-group label="工種名" label-for="edit-work-type-name">
                  <b-form-input
                    id="edit-work-type-name"
                    v-model="editMeasureForm.workTypeName"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="作業名" label-for="edit-work-name">
                  <b-form-input
                    id="edit-work-name"
                    v-model="editMeasureForm.workName"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <b-form-group label="危険性・有害性（予想される災害）" label-for="edit-hazards">
                  <b-form-textarea
                    id="edit-hazards"
                    v-model="editMeasureForm.hazards"
                    rows="3"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="除去・低減策（防止対策）" label-for="edit-prevention-measures">
                  <b-form-textarea
                    id="edit-prevention-measures"
                    v-model="editMeasureForm.preventionMeasures"
                    rows="3"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>

          <b-card title="責任者">
            <b-row>
              <b-col md="5">
                <b-form-group label="契約会社" label-for="edit-responsible-contract-company">
                  <b-form-select
                    id="edit-responsible-contract-company"
                    v-model="editResponsibleForm.contractCompanyId"
                    :options="siteContractCompanyOptions"
                  />
                </b-form-group>
              </b-col>
              <b-col md="5">
                <b-form-group label="責任者名" label-for="edit-responsible-name">
                  <b-form-input
                    id="edit-responsible-name"
                    v-model="editResponsibleForm.responsibleName"
                  />
                </b-form-group>
              </b-col>
              <b-col md="2" class="d-flex align-items-end">
                <b-button @click="saveResponsibleFromEditModal" variant="primary" class="mb-3">
                  {{ editResponsibleForm.id ? '更新' : '追加' }}
                </b-button>
                <b-button v-if="editResponsibleForm.id" @click="cancelEditResponsibleEdit" variant="secondary" class="mb-3 ml-2">
                  取消
                </b-button>
              </b-col>
            </b-row>

            <b-table
              :items="editMeasureResponsibles"
              :fields="responsibleFields"
              striped
              hover
              responsive
            >
              <template #cell(contractCompanyName)="row">
                {{ row.item.contractCompanyName || '-' }}
              </template>
              <template #cell(responsibleName)="row">
                {{ row.item.responsibleName || '-' }}
              </template>
              <template #cell(actions)="row">
                <b-button size="sm" @click="editResponsibleFromEditModal(row.item)" variant="info" class="mr-1">
                  編集
                </b-button>
                <b-button size="sm" @click="deleteResponsibleFromEditModal(row.item)" variant="danger">
                  削除
                </b-button>
              </template>
            </b-table>
          </b-card>
        </b-form>

        <template #modal-footer>
          <div class="d-flex w-100 justify-content-between align-items-center">
            <b-form-checkbox
              v-model="editMeasureForm.copyToKy"
              switch
            >
              KY転記
            </b-form-checkbox>
            <div>
              <b-button variant="secondary" @click="$bvModal.hide('edit-measure-modal')" class="mr-2">
                キャンセル
              </b-button>
              <b-button variant="primary" @click="updateMeasureFromModal">
                保存
              </b-button>
            </div>
          </div>
        </template>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import SiteSafetyHealthMeasureApi from '@/api/SiteSafetyHealthMeasureApi'
import ContractCompanyApi from '@/api/ContractCompanyApi'
import DiaryListConstants from '@/store/constants/diaryListRecords'
import SummaryForm from '@/components/SiteSafetyHealthSummary/SummaryForm.vue'
import draggable from 'vuedraggable'
import BackToListLink from '@/components/shared/BackToListLink'

export default {
  name: 'SiteSafetyHealthMeasures',
  components: {
    SummaryForm,
    draggable,
    BackToListLink
  },
    data() {
      return {
        selectedSiteId: null,
        targetMonth: this.getCurrentMonth(),
        siteContractCompanies: [],
        siteMeasures: [],
        selectedMeasure: null,
        measureResponsibles: [],
        siteMeasureForm: {
          id: null,
          workTypeName: '',
          workName: '',
          hazards: '',
          preventionMeasures: '',
          personInCharge: '',
          copyToKy: false
        },
        responsibleForm: {
          id: null,
          contractCompanyId: null,
          responsibleName: ''
        },
        editMeasureForm: {
          id: null,
          workTypeName: '',
          workName: '',
          hazards: '',
          preventionMeasures: '',
          personInCharge: '',
          copyToKy: false
        },
        editResponsibleForm: {
          id: null,
          contractCompanyId: null,
          responsibleName: ''
        },
        editMeasureResponsibles: [],
        selectedEditMeasure: null,
        isTogglingCopyToKy: null,
        responsibleFields: [
          { key: 'contractCompanyName', label: '契約会社' },
          { key: 'responsibleName', label: '責任者名' },
          { key: 'actions', label: '操作' }
        ]
      }
    },
    computed: {
      sites() {
        return this.$store.getters[DiaryListConstants.GETTERS.getSites] || []
      },
      siteOptions() {
        return [
          { value: null, text: '現場を選択してください' },
          ...this.sites.map(site => ({
            value: site.id,
            text: site.name
          }))
        ]
      },
      siteContractCompanyOptions() {
        return [
          { value: null, text: '契約会社を選択してください' },
          ...this.siteContractCompanies.map(company => ({
            value: company.id,
            text: company.name
          }))
        ]
      },
      isManagedUser() {
        return !localStorage.getItem('contractor_token')
      }
    },
    async mounted() {
      await this.$store.dispatch(DiaryListConstants.DISPATCH.getSiteRecords)
      await this.$store.dispatch(DiaryListConstants.DISPATCH.getMeRecord)
    
      const siteId = this.$route.query.siteId
      if (siteId) {
        this.selectedSiteId = siteId
        await this.loadSiteContractCompanies()
        if (this.targetMonth) {
          await this.loadMeasures()
        }
      }
    },
  methods: {
        getCurrentMonth() {
          const now = new Date()
          const year = now.getFullYear()
          const month = String(now.getMonth() + 1).padStart(2, '0')
          return `${year}-${month}`
        },
        async loadSiteContractCompanies() {
          if (!this.selectedSiteId) {
            this.siteContractCompanies = []
            return
          }
          try {
            const response = await ContractCompanyApi.searchContractCompanies({ siteId: this.selectedSiteId })
            this.siteContractCompanies = response.data.contractCompanies || []
          } catch (error) {
            console.error('Failed to load site contract companies:', error)
            this.$bvToast.toast('現場の契約会社の読み込みに失敗しました', {
              title: 'エラー',
              variant: 'danger'
            })
          }
        },
        async onSiteChange() {
          await this.loadSiteContractCompanies()
          if (this.selectedSiteId && this.targetMonth) {
            await this.loadMeasures()
          }
        },
        async onMonthChange() {
          if (this.selectedSiteId && this.targetMonth) {
            await this.loadMeasures()
          }
        },
        async loadMeasures() {
          await this.loadSiteMeasures()
        },
        async loadSiteMeasures() {
          try {
            const targetDate = new Date(this.targetMonth + '-01')
            const response = await SiteSafetyHealthMeasureApi.searchSiteMeasures({
              siteId: this.selectedSiteId,
              targetMonth: targetDate.toISOString().split('T')[0]
            })
            const measures = response.data.measures || []
            this.siteMeasures = measures.map(measure => ({
              ...measure,
              responsibles: (measure.responsibles || []).map(r => ({
                ...r,
                contractCompanyName: this.siteContractCompanies.find(c => c.id === r.contractCompanyId)?.name || ''
              }))
            }))
          } catch (error) {
            console.error('Failed to load site measures:', error)
            this.$bvToast.toast('現場への安全衛生指示の読み込みに失敗しました', {
              title: 'エラー',
              variant: 'danger'
            })
          }
        },
    async saveSiteMeasure() {
      if (!this.selectedSiteId || !this.targetMonth) {
        this.$bvToast.toast('現場と対象月を選択してください', {
          title: 'エラー',
          variant: 'warning'
        })
        return
      }

      try {
        const targetDate = new Date(this.targetMonth + '-01')
        const data = {
          siteId: this.selectedSiteId,
          targetMonth: targetDate.toISOString().split('T')[0],
          workTypeName: this.siteMeasureForm.workTypeName || null,
          workName: this.siteMeasureForm.workName || null,
          hazards: this.siteMeasureForm.hazards || null,
          preventionMeasures: this.siteMeasureForm.preventionMeasures || null,
          personInCharge: this.siteMeasureForm.personInCharge || null,
          copyToKy: this.siteMeasureForm.copyToKy
        }

        if (this.siteMeasureForm.id) {
          await SiteSafetyHealthMeasureApi.updateSiteMeasure(this.siteMeasureForm.id, {
            workTypeName: data.workTypeName,
            workName: data.workName,
            hazards: data.hazards,
            preventionMeasures: data.preventionMeasures,
            personInCharge: data.personInCharge,
            copyToKy: this.siteMeasureForm.copyToKy
          })
          this.$bvToast.toast('現場への安全衛生指示を更新しました', {
            title: '成功',
            variant: 'success'
          })
        } else {
          await SiteSafetyHealthMeasureApi.createSiteMeasure(data)
          this.$bvToast.toast('現場への安全衛生指示を保存しました', {
            title: '成功',
            variant: 'success'
          })
        }

        this.siteMeasureForm = { id: null, workTypeName: '', workName: '', hazards: '', preventionMeasures: '', personInCharge: '', copyToKy: false }
        await this.loadSiteMeasures()
      } catch (error) {
        console.error('Failed to save site measure:', error)
        this.$bvToast.toast('現場への安全衛生指示の保存に失敗しました', {
          title: 'エラー',
          variant: 'danger'
        })
      }
    },
    async saveContractCompanyMeasure() {
      if (!this.selectedSiteId || !this.targetMonth || !this.contractCompanyMeasureForm.contractCompanyId) {
        this.$bvToast.toast('現場、対象月、契約会社を選択してください', {
          title: 'エラー',
          variant: 'warning'
        })
        return
      }

      try {
        const targetDate = new Date(this.targetMonth + '-01')
        const data = {
          siteId: this.selectedSiteId,
          contractCompanyId: this.contractCompanyMeasureForm.contractCompanyId,
          targetMonth: targetDate.toISOString().split('T')[0],
          workTypeName: this.contractCompanyMeasureForm.workTypeName || null,
          workName: this.contractCompanyMeasureForm.workName || null,
          hazards: this.contractCompanyMeasureForm.hazards || null,
          preventionMeasures: this.contractCompanyMeasureForm.preventionMeasures || null,
          personInCharge: this.contractCompanyMeasureForm.personInCharge || null
        }

        if (this.contractCompanyMeasureForm.id) {
          await SiteSafetyHealthMeasureApi.updateContractCompanyMeasure(this.contractCompanyMeasureForm.id, {
            workTypeName: data.workTypeName,
            workName: data.workName,
            hazards: data.hazards,
            preventionMeasures: data.preventionMeasures,
            personInCharge: data.personInCharge
          })
          this.$bvToast.toast('契約会社への安全衛生指示を更新しました', {
            title: '成功',
            variant: 'success'
          })
        } else {
          await SiteSafetyHealthMeasureApi.createContractCompanyMeasure(data)
          this.$bvToast.toast('契約会社への安全衛生指示を保存しました', {
            title: '成功',
            variant: 'success'
          })
        }

        this.contractCompanyMeasureForm = { id: null, contractCompanyId: null, workTypeName: '', workName: '', hazards: '', preventionMeasures: '', personInCharge: '' }
        await this.loadContractCompanyMeasures()
      } catch (error) {
        console.error('Failed to save contract company measure:', error)
        this.$bvToast.toast('契約会社への安全衛生指示の保存に失敗しました', {
          title: 'エラー',
          variant: 'danger'
        })
      }
    },
    editSiteMeasure(measure) {
      this.siteMeasureForm = {
        id: measure.id,
        workTypeName: measure.workTypeName || '',
        workName: measure.workName || '',
        hazards: measure.hazards || '',
        preventionMeasures: measure.preventionMeasures || '',
        personInCharge: measure.personInCharge || '',
        copyToKy: measure.copyToKy || false
      }
    },
    cancelSiteMeasureEdit() {
      this.siteMeasureForm = { id: null, workTypeName: '', workName: '', hazards: '', preventionMeasures: '', personInCharge: '', copyToKy: false }
    },
        async deleteSiteMeasure(measure) {
          const confirmed = await this.$bvModal.msgBoxConfirm(
            '本当に削除しますか？',
            {
              title: '確認',
              okVariant: 'danger',
              okTitle: '削除',
              cancelTitle: 'キャンセル'
            }
          )

          if (confirmed) {
            try {
              await SiteSafetyHealthMeasureApi.deleteSiteMeasure(measure.id)
              this.$bvToast.toast('現場への安全衛生指示を削除しました', {
                title: '成功',
                variant: 'success'
              })
              await this.loadSiteMeasures()
            } catch (error) {
              console.error('Failed to delete site measure:', error)
              this.$bvToast.toast('現場への安全衛生指示の削除に失敗しました', {
                title: 'エラー',
                variant: 'danger'
              })
            }
          }
        },
        async openResponsibleModal(measure) {
          this.selectedMeasure = measure
          this.measureResponsibles = (measure.responsibles || []).map(r => ({
            ...r,
            contractCompanyName: this.siteContractCompanies.find(c => c.id === r.contractCompanyId)?.name || ''
          }))
          this.responsibleForm = { id: null, contractCompanyId: null, responsibleName: '' }
          this.$bvModal.show('responsible-modal')
        },
        onResponsibleModalHidden() {
          this.selectedMeasure = null
          this.measureResponsibles = []
          this.responsibleForm = { id: null, contractCompanyId: null, responsibleName: '' }
        },
        async saveResponsible() {
          if (!this.selectedMeasure) {
            return
          }

          try {
            const data = {
              contractCompanyId: this.responsibleForm.contractCompanyId || null,
              responsibleName: this.responsibleForm.responsibleName || null
            }

            if (this.responsibleForm.id) {
              await SiteSafetyHealthMeasureApi.updateResponsible(
                this.selectedMeasure.id,
                this.responsibleForm.id,
                data
              )
              this.$bvToast.toast('責任者を更新しました', {
                title: '成功',
                variant: 'success'
              })
            } else {
              await SiteSafetyHealthMeasureApi.createResponsible(this.selectedMeasure.id, data)
              this.$bvToast.toast('責任者を追加しました', {
                title: '成功',
                variant: 'success'
              })
            }

            this.responsibleForm = { id: null, contractCompanyId: null, responsibleName: '' }
            await this.loadSiteMeasures()
            const updatedMeasure = this.siteMeasures.find(m => m.id === this.selectedMeasure.id)
            if (updatedMeasure) {
              this.measureResponsibles = (updatedMeasure.responsibles || []).map(r => ({
                ...r,
                contractCompanyName: this.siteContractCompanies.find(c => c.id === r.contractCompanyId)?.name || ''
              }))
            }
          } catch (error) {
            console.error('Failed to save responsible:', error)
            this.$bvToast.toast('責任者の保存に失敗しました', {
              title: 'エラー',
              variant: 'danger'
            })
          }
        },
        editResponsible(responsible) {
          this.responsibleForm = {
            id: responsible.id,
            contractCompanyId: responsible.contractCompanyId || null,
            responsibleName: responsible.responsibleName || ''
          }
        },
        cancelResponsibleEdit() {
          this.responsibleForm = { id: null, contractCompanyId: null, responsibleName: '' }
        },
        async deleteResponsible(responsible) {
          if (!this.selectedMeasure) {
            return
          }

          const confirmed = await this.$bvModal.msgBoxConfirm(
            '本当に削除しますか？',
            {
              title: '確認',
              okVariant: 'danger',
              okTitle: '削除',
              cancelTitle: 'キャンセル'
            }
          )

          if (confirmed) {
            try {
              await SiteSafetyHealthMeasureApi.deleteResponsible(this.selectedMeasure.id, responsible.id)
              this.$bvToast.toast('責任者を削除しました', {
                title: '成功',
                variant: 'success'
              })
              await this.loadSiteMeasures()
              const updatedMeasure = this.siteMeasures.find(m => m.id === this.selectedMeasure.id)
              if (updatedMeasure) {
                this.measureResponsibles = (updatedMeasure.responsibles || []).map(r => ({
                  ...r,
                  contractCompanyName: this.siteContractCompanies.find(c => c.id === r.contractCompanyId)?.name || ''
                }))
              }
            } catch (error) {
              console.error('Failed to delete responsible:', error)
              this.$bvToast.toast('責任者の削除に失敗しました', {
                title: 'エラー',
                variant: 'danger'
              })
            }
          }
        },
        openEditMeasureModal(measure) {
          this.selectedEditMeasure = measure
          this.editMeasureForm = {
            id: measure.id,
            workTypeName: measure.workTypeName || '',
            workName: measure.workName || '',
            hazards: measure.hazards || '',
            preventionMeasures: measure.preventionMeasures || '',
            personInCharge: measure.personInCharge || '',
            copyToKy: measure.copyToKy || false
          }
          this.editMeasureResponsibles = (measure.responsibles || []).map(r => ({
            ...r,
            contractCompanyName: this.siteContractCompanies.find(c => c.id === r.contractCompanyId)?.name || ''
          }))
          this.editResponsibleForm = { id: null, contractCompanyId: null, responsibleName: '' }
          this.$bvModal.show('edit-measure-modal')
        },
        onEditMeasureModalHidden() {
          this.selectedEditMeasure = null
          this.editMeasureForm = { id: null, workTypeName: '', workName: '', hazards: '', preventionMeasures: '', personInCharge: '', copyToKy: false }
          this.editMeasureResponsibles = []
          this.editResponsibleForm = { id: null, contractCompanyId: null, responsibleName: '' }
        },
        async updateMeasureFromModal() {
          if (!this.selectedEditMeasure) {
            return
          }

          try {
            await SiteSafetyHealthMeasureApi.updateSiteMeasure(this.editMeasureForm.id, {
              workTypeName: this.editMeasureForm.workTypeName || null,
              workName: this.editMeasureForm.workName || null,
              hazards: this.editMeasureForm.hazards || null,
              preventionMeasures: this.editMeasureForm.preventionMeasures || null,
              personInCharge: this.editMeasureForm.personInCharge || null,
              copyToKy: this.editMeasureForm.copyToKy
            })
            this.$bvToast.toast('安全衛生指示を更新しました', {
              title: '成功',
              variant: 'success'
            })
            await this.loadSiteMeasures()
            this.$bvModal.hide('edit-measure-modal')
          } catch (error) {
            console.error('Failed to update site measure:', error)
            this.$bvToast.toast('安全衛生指示の更新に失敗しました', {
              title: 'エラー',
              variant: 'danger'
            })
          }
        },
        async saveResponsibleFromEditModal() {
          if (!this.selectedEditMeasure) {
            return
          }

          try {
            const data = {
              contractCompanyId: this.editResponsibleForm.contractCompanyId || null,
              responsibleName: this.editResponsibleForm.responsibleName || null
            }

            if (this.editResponsibleForm.id) {
              await SiteSafetyHealthMeasureApi.updateResponsible(
                this.selectedEditMeasure.id,
                this.editResponsibleForm.id,
                data
              )
              this.$bvToast.toast('責任者を更新しました', {
                title: '成功',
                variant: 'success'
              })
            } else {
              await SiteSafetyHealthMeasureApi.createResponsible(this.selectedEditMeasure.id, data)
              this.$bvToast.toast('責任者を追加しました', {
                title: '成功',
                variant: 'success'
              })
            }

            this.editResponsibleForm = { id: null, contractCompanyId: null, responsibleName: '' }
            await this.loadSiteMeasures()
            const updatedMeasure = this.siteMeasures.find(m => m.id === this.selectedEditMeasure.id)
            if (updatedMeasure) {
              this.editMeasureResponsibles = (updatedMeasure.responsibles || []).map(r => ({
                ...r,
                contractCompanyName: this.siteContractCompanies.find(c => c.id === r.contractCompanyId)?.name || ''
              }))
            }
          } catch (error) {
            console.error('Failed to save responsible:', error)
            this.$bvToast.toast('責任者の保存に失敗しました', {
              title: 'エラー',
              variant: 'danger'
            })
          }
        },
        editResponsibleFromEditModal(responsible) {
          this.editResponsibleForm = {
            id: responsible.id,
            contractCompanyId: responsible.contractCompanyId || null,
            responsibleName: responsible.responsibleName || ''
          }
        },
        cancelEditResponsibleEdit() {
          this.editResponsibleForm = { id: null, contractCompanyId: null, responsibleName: '' }
        },
        async deleteResponsibleFromEditModal(responsible) {
          if (!this.selectedEditMeasure) {
            return
          }

          const confirmed = await this.$bvModal.msgBoxConfirm(
            '本当に削除しますか？',
            {
              title: '確認',
              okVariant: 'danger',
              okTitle: '削除',
              cancelTitle: 'キャンセル'
            }
          )

          if (confirmed) {
            try {
              await SiteSafetyHealthMeasureApi.deleteResponsible(this.selectedEditMeasure.id, responsible.id)
              this.$bvToast.toast('責任者を削除しました', {
                title: '成功',
                variant: 'success'
              })
              await this.loadSiteMeasures()
              const updatedMeasure = this.siteMeasures.find(m => m.id === this.selectedEditMeasure.id)
              if (updatedMeasure) {
                this.editMeasureResponsibles = (updatedMeasure.responsibles || []).map(r => ({
                  ...r,
                  contractCompanyName: this.siteContractCompanies.find(c => c.id === r.contractCompanyId)?.name || ''
                }))
              }
            } catch (error) {
              console.error('Failed to delete responsible:', error)
              this.$bvToast.toast('責任者の削除に失敗しました', {
                title: 'エラー',
                variant: 'danger'
              })
            }
          }
        },
        async toggleCopyToKy(measure) {
          this.isTogglingCopyToKy = measure.id
          try {
            await SiteSafetyHealthMeasureApi.updateSiteMeasure(measure.id, {
              workTypeName: measure.workTypeName || null,
              workName: measure.workName || null,
              hazards: measure.hazards || null,
              preventionMeasures: measure.preventionMeasures || null,
              personInCharge: measure.personInCharge || null,
              copyToKy: !measure.copyToKy,
            })
            measure.copyToKy = !measure.copyToKy
            this.$bvToast.toast(
              `KY転記を${measure.copyToKy ? 'オン' : 'オフ'}にしました`,
              {
                title: '更新完了',
                variant: 'success',
                solid: true,
              }
            )
          } catch (error) {
            console.error('Failed to toggle copyToKy:', error)
            this.$bvToast.toast('KY転記の更新に失敗しました', {
              title: 'エラー',
              variant: 'danger',
              solid: true,
            })
          } finally {
            this.isTogglingCopyToKy = null
          }
        },
        async onDragEnd() {
          if (!this.selectedSiteId) {
            return
          }
          try {
            const orders = this.siteMeasures.map((measure, index) => ({
              id: measure.id,
              displayOrder: index + 1
            }))
            await SiteSafetyHealthMeasureApi.reorderSiteMeasures({
              siteId: this.selectedSiteId,
              orders
            })
            this.$bvToast.toast('表示順を更新しました', {
              title: '成功',
              variant: 'success'
            })
          } catch (error) {
            console.error('Failed to reorder measures:', error)
            this.$bvToast.toast('表示順の更新に失敗しました', {
              title: 'エラー',
              variant: 'danger'
            })
            await this.loadSiteMeasures()
          }
        },
        async downloadSafetyPosterExcel() {
      if (!this.selectedSiteId || !this.targetMonth) {
        console.error('Missing siteId or targetMonth for safety poster Excel generation')
        return
      }

      try {
        const response = await SiteSafetyHealthMeasureApi.generateSafetyPosterExcel(
          this.selectedSiteId,
          this.targetMonth
        )
        
        console.log('Safety poster Excel generated successfully:', response)
        
        const { excelData, fileName } = response.data
        
        if (excelData && fileName) {
          // Convert Base64 to Blob
          const byteCharacters = atob(excelData)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          })

          // Create download link
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = fileName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
          
          this.$bvToast.toast('安全ポスターExcelをダウンロードしました', {
            title: '成功',
            variant: 'success'
          })
        } else {
          console.error('Excel data or filename not received from server')
          this.$bvToast.toast('Excelデータの取得に失敗しました', {
            title: 'エラー',
            variant: 'danger'
          })
        }
      } catch (error) {
        console.error('Failed to generate safety poster Excel:', error)
        this.$bvToast.toast('安全ポスターExcelの生成に失敗しました', {
          title: 'エラー',
          variant: 'danger'
        })
      }
    }
  }
}
</script>

<style scoped>
.site-safety-health-measures {
  padding: 20px;
}
</style>
